import { UseQueryOptions } from '@tanstack/react-query';
import {
  CACHE_TIME_MS,
  LONG_CACHE_TIME_MS,
  SHORT_CACHE_TIME_MS,
} from '@/constants/timings';

/**
 * Common React Query configuration options
 * Provides consistent defaults across all hooks
 */

/**
 * Default query options for standard data fetching
 * - 5 minute cache
 * - No refetch on mount or window focus
 * - Single retry attempt
 */
export const defaultQueryOptions = {
  staleTime: CACHE_TIME_MS,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: 1,
};

/**
 * Query options for long-lived data (user preferences, locations, etc.)
 * - 30 minute cache
 * - No refetch on mount or window focus
 * - No retry
 */
export const longCacheQueryOptions = {
  staleTime: LONG_CACHE_TIME_MS,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: false,
};

/**
 * Query options for frequently changing data (availability, real-time data)
 * - 1 minute cache
 * - No refetch on mount or window focus
 * - No retry
 */
export const shortCacheQueryOptions = {
  staleTime: SHORT_CACHE_TIME_MS,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: false,
};

/**
 * Query options for dashboard/critical data
 * - 5 minute cache
 * - Refetch on mount
 * - No refetch on window focus
 * - Single retry
 */
export const dashboardQueryOptions = {
  staleTime: CACHE_TIME_MS,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
  retry: 1,
};

/**
 * Merge custom options with a base configuration
 */
export function mergeQueryOptions<T>(
  baseOptions: Partial<UseQueryOptions<T>>,
  customOptions: Partial<UseQueryOptions<T>> = {}
): Partial<UseQueryOptions<T>> {
  return {
    ...baseOptions,
    ...customOptions,
  };
}
